import {Day, isSameMonth, startOfMonth} from 'date-fns';
import {Box, Hide, HStack, Stack, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useLocale} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {useState, useRef, ClipboardEvent, FocusEvent, KeyboardEvent} from 'react';

import {always, isEmpty, isNotNil} from 'ramda';
import {isNotNilOrEmpty, isTrue} from 'ramda-adjunct';

import {useBoolean, suffixTestId, TestIdProps, Nullish, useOnMount} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {Placement} from '../../types/Placement';
import {useAnimatedPopper} from '../AnimatedPopper/hooks/useAnimatedPopper';
import {Button} from '../Button/Button';
import {CalendarControl} from '../Calendar/components/CalendarControl/CalendarControl';
import {CalendarView} from '../Calendar/components/CalendarView/CalendarView';
import {useCalendarKeyboardNavigation} from '../Calendar/hooks/useCalendarKeyboardNavigation';
import {CalendarFormValue, DisabledDate} from '../Calendar/types';
import {isDisabledDate} from '../Calendar/utils/isDisabledDate';
import {Separator, SeparatorProps} from '../Separator/Separator';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {DatePickerInput} from './components/DatePickerInput';
import {DatePickerRelativeDates} from './components/DatePickerRelativeDates';
import {
  useDatePickerFocusTrap,
  FocusTrapConfig,
  FocusZoneType,
} from './hooks/useDatePickerFocusTrap';
import {RelativeDate} from './RelativeDate';
import {RETURN_FOCUS_DELAY_MS} from './returnFocusDelayMs';
import {getDateFromStringInput} from './utils/getDateFromStringInput';
import {getDefaultRelativeDates} from './utils/getDefaultRelativeDates';
import {getInitialActiveMonth} from './utils/getInitialActiveMonth';
import {getInitialCalendarFocusDate} from './utils/getInitialCalendarFocusDate';
import {isReactSelectMenuClick} from './utils/isReactSelectMenuClick';

const POPPER_VIEWPORT_PADDING = 8;

const resolveInitialFocusZone = (
  value: Date | Nullish,
  hasRelativeDates: boolean
): FocusZoneType => {
  if (isNotNil(value)) {
    return 'calendar';
  }
  if (hasRelativeDates) {
    return 'relative-dates';
  }
  return 'calendar';
};

export interface DatePickerProps extends FormControlProps<Date | null>, TestIdProps {
  disabledWeekdays?: Day[];
  disabledDates?: DisabledDate[];
  minDate?: Date;
  maxDate?: Date;
  placement?: Placement;
  relativeDates?: RelativeDate[];
  isRelativeDatesHidden?: boolean;
  isRecommended?: boolean;
  placeholder?: string;
  onRelativeDayFocus?: (index: number) => void;
  onRelativeDayBlur?: () => void;
  keyboardNavigationEnabled?: boolean;
  focusTrapEnabled?: boolean;
  focusTrapConfig?: Partial<FocusTrapConfig>;
  isNonClearable?: boolean;
  stackDirection?: 'column' | 'row';
}

export function DatePicker(props: DatePickerProps) {
  const {localeConfig, language} = useLocale();
  const formatDate = useDateTimeFormatter();

  const t = useTranslationContext();
  const [inputValue, setInputValue] = useState<string | null>(null);
  const [isFocused, focusInput, blurInput] = useBoolean();
  const inputRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  // Focus management finds the calendar controls/close button by data-testid, so one must
  // always exist or Tab can't leave the grid. Wrappers like DateTimePicker forward an empty
  // string, so cover empty too — not just nullish. The lookup is popper-scoped, so the
  // shared fallback never collides across instances.
  const testIdProps = {
    'data-testid': isNotNilOrEmpty(props['data-testid']) ? props['data-testid'] : 'datePicker',
  };

  const {
    popperProps,
    Popper,
    openPopper,
    closePopper: originalClosePopper,
    referenceRef,
    isOpen,
  } = useAnimatedPopper({
    gutter: 4,
    placement: props.placement ?? 'bottom-start',
    // Re-anchor to the input on scroll/resize; without it Floating UI positions the popper
    // once and it stays stranded at its initial screen coordinates while the page scrolls.
    autoUpdateOptions: 'default',
    shiftOptions: {
      padding: POPPER_VIEWPORT_PADDING,
      crossAxis: true,
    },
    shouldIgnoreOutsideClick: isReactSelectMenuClick,
  });

  const closePopper = (options?: {restoreInputFocus?: boolean}) => {
    originalClosePopper();
    setRelativeDaysFocused(false);
    setFocusedRelativeDayIndex(-1);
    setCalendarFocusedDate(null);
    setShouldInitializePopperFocus(false);
    if (options?.restoreInputFocus !== false) {
      returnFocusToInput(RETURN_FOCUS_DELAY_MS);
    }
  };

  const formattedValue = props.value ? formatDate('dateShort', props.value) : '';

  const placeholder = match([isNotNilOrEmpty(props.placeholder), isTrue(props.isDisabled)])
    .with([true, Pattern.boolean], always(props.placeholder))
    .with([false, true], always(null))
    .otherwise(always(t('general.labels.select')));

  const [activeMonth, setActiveMonth] = useState(() =>
    getInitialActiveMonth(props.value, props.minDate, props.maxDate)
  );

  const [calendarFocusedDate, setCalendarFocusedDate] = useState<Date | null>(null);
  const [relativeDaysFocused, setRelativeDaysFocused] = useState(false);
  const [focusedRelativeDayIndex, setFocusedRelativeDayIndex] = useState(-1);
  const [shouldInitializePopperFocus, setShouldInitializePopperFocus] = useState(false);
  const blurDeferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useOnMount(function registerBlurDeferTimeoutCleanup() {
    return function clearBlurDeferTimeoutOnUnmount() {
      if (blurDeferTimeoutRef.current !== null) {
        clearTimeout(blurDeferTimeoutRef.current);
      }
    };
  });

  const returnFocusToInput = (delay = 0) => {
    if (inputRef.current && !props.isDisabled) {
      if (delay > 0) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, delay);
      } else {
        inputRef.current.focus();
      }
    }
  };

  const onChange = (value: CalendarFormValue) => {
    if (Array.isArray(value)) {
      return;
    }

    // Blur so closePopper's returnFocusToInput counts as a fresh focus and handleFocus
    // re-seeds inputValue from the newly committed value instead of the stale null below.
    blurInput();
    closePopper();
    props.onChange?.(value);
    setInputValue(null);
    setCalendarFocusedDate(null);
  };

  const handleOnClearInput = () => {
    if (props.isNonClearable) {
      return;
    }

    blurInput();
    props.onChange?.(null);
    closePopper();
    setInputValue(null);
    setCalendarFocusedDate(null);
  };

  const handleRelativeDatesFocusChange = (focused: boolean, index?: number) => {
    setRelativeDaysFocused(focused);
    if (focused && index !== undefined) {
      setFocusedRelativeDayIndex(index);
      props.onRelativeDayFocus?.(index);
    } else {
      setFocusedRelativeDayIndex(-1);
      if (!focused) {
        props.onRelativeDayBlur?.();
      }
    }
  };

  const handleRelativeDayFocusChange = (index: number) => {
    handleRelativeDatesFocusChange(true, index);
  };

  const handleRelativeDayBlur = () => {
    handleRelativeDatesFocusChange(false);
  };

  const handleRelativeDayEscape = () => {
    closePopper();
    setCalendarFocusedDate(null);
  };

  const onOpen = (options?: {initializePopperFocus?: boolean}) => {
    if (props.isDisabled) {
      return;
    }
    setShouldInitializePopperFocus(options?.initializePopperFocus ?? false);
    openPopper();
    if (props.value && !isSameMonth(activeMonth, props.value)) {
      setActiveMonth(startOfMonth(props.value ?? new Date()));
    }
  };

  const relativeDates =
    isOpen && !props.isRelativeDatesHidden
      ? (props.relativeDates ?? getDefaultRelativeDates(t))
      : [];

  const enabledRelativeDates = relativeDates.filter(
    (relativeDate) => !isDisabledDate(relativeDate.date, props)
  );

  const getCalendarFocusDate = () =>
    getInitialCalendarFocusDate(props.value, activeMonth, {
      disabledDates: props.disabledDates,
      disabledWeekdays: props.disabledWeekdays,
      minDate: props.minDate,
      maxDate: props.maxDate,
    });

  const keyboardNavigation = useCalendarKeyboardNavigation({
    activeMonth,
    focusedDate: calendarFocusedDate,
    onFocusChange: setCalendarFocusedDate,
    onDateSelect: onChange,
    onMonthChange: setActiveMonth,
    minDate: props.minDate,
    maxDate: props.maxDate,
    disabledDates: props.disabledDates,
  });

  const focusTrap = useDatePickerFocusTrap({
    isOpen,
    onClose: closePopper,
    inputRef,
    popperRef,
    hasRelativeDates: enabledRelativeDates.length > 0,
    relativeDatesFocused: relativeDaysFocused,
    focusedRelativeDayIndex,
    calendarFocusedDate,
    onRelativeDatesFocusChange: handleRelativeDatesFocusChange,
    onCalendarFocusChange: setCalendarFocusedDate,
    defaultCalendarDate: getCalendarFocusDate(),
    shouldInitializePopperFocus,
    config: {
      enabled: props.focusTrapEnabled ?? true,
      initialFocusZone: resolveInitialFocusZone(props.value, enabledRelativeDates.length > 0),
      ...props.focusTrapConfig,
    },
  });

  const handlePopperKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // Let focus trap handle Tab and Escape
    focusTrap.handlePopperKeyDown(event);

    // If focus trap handled the event, don't continue
    if (event.key === 'Tab' || event.key === 'Escape') {
      return;
    }

    // Handle other keyboard navigation (arrows, Enter, Space, etc.)
    if (relativeDaysFocused) {
      // Let DatePickerRelativeDates handle its own keyboard navigation
      return;
    }

    // Let calendar handle arrow keys and other navigation
    keyboardNavigation.onKeyDown(event);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData?.getData('text/plain');
    const parsedDate = getDateFromStringInput(pastedText, localeConfig, language);

    if (parsedDate) {
      event.preventDefault();
      props.onChange?.(parsedDate);
      setInputValue(null);
      blurInput();
      closePopper({restoreInputFocus: false});
    }
  };

  const commitInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    blurInput();
    const inputValue = event.target.value;
    const isInputChanged = inputValue !== formattedValue;
    const parsedDate = getDateFromStringInput(inputValue, localeConfig, language);

    if (isInputChanged) {
      props.onChange?.(parsedDate);
      // User is tabbing away — closing the popper must not pull focus back, or Tab breaks.
      closePopper({restoreInputFocus: false});
      setInputValue(null);
    }

    props.onBlur?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;

    if (isOpen && relatedTarget && popperRef.current?.contains(relatedTarget)) {
      return;
    }

    // Programmatic focus into the popper can blur the input with a null relatedTarget
    if (isOpen && !relatedTarget) {
      if (isNotNil(blurDeferTimeoutRef.current)) {
        clearTimeout(blurDeferTimeoutRef.current);
      }

      blurDeferTimeoutRef.current = setTimeout(() => {
        blurDeferTimeoutRef.current = null;

        if (popperRef.current?.contains(document.activeElement)) {
          return;
        }

        commitInputBlur(event);
      }, 0);
      return;
    }

    commitInputBlur(event);
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    // Re-seed on a genuinely fresh focus. Skip it on the mid-edit popper round-trip (still
    // focused: user backspaced, focus dipped into the popper, returnFocusToInput refires
    // this) — re-seeding there would resurrect the value the user just deleted.
    const wasFocused = isFocused;
    focusInput();
    if (!wasFocused) {
      setInputValue(formattedValue);
    }
    props.onFocus?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isOpen) {
        onOpen({initializePopperFocus: true});
      }
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation(); // Prevent ESC from bubbling to parent modals
      if (isOpen) {
        closePopper();
        setCalendarFocusedDate(null);
      }
    }
    // Handle Tab when popup is open to move focus to the popup
    if (event.key === 'Tab' && isOpen && !event.shiftKey) {
      event.preventDefault();
      if (isNotNil(props.value)) {
        setCalendarFocusedDate(getCalendarFocusDate());
      } else if (enabledRelativeDates.length > 0) {
        handleRelativeDatesFocusChange(true, 0);
      } else {
        setCalendarFocusedDate(getCalendarFocusDate());
      }
    }
  };

  const relativeDateSeparatorOrientation = match<
    DatePickerProps['stackDirection'],
    SeparatorProps['orientation']
  >(props.stackDirection)
    .with('column', () => 'horizontal')
    .with('row', () => 'vertical')
    .otherwise(() => ['horizontal', 'vertical']);

  return (
    <div>
      <div ref={referenceRef}>
        <DatePickerInput
          ref={inputRef}
          id={props.id}
          onClick={onOpen}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          leftIcon="action/date_range"
          value={isFocused ? inputValue : formattedValue}
          onChange={setInputValue}
          label={props.label}
          tooltip={props.tooltip}
          isInvalid={props.isInvalid}
          isRequired={props.isRequired}
          isDisabled={props.isDisabled}
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          placeholder={placeholder}
          autoComplete="off"
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('input', testIdProps)}
          onClearInput={props.isNonClearable ? undefined : handleOnClearInput}
        />
      </div>
      <Popper {...popperProps}>
        <div
          ref={popperRef}
          onKeyDown={handlePopperKeyDown}
          tabIndex={-1}
          role="dialog"
          aria-modal={focusTrap.focusTrapProps['aria-modal']}
          aria-label={focusTrap.focusTrapProps['aria-label']}
        >
          <Box
            borderRadius="medium"
            boxShadow="elevation_2"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            backgroundColor="general.white"
          >
            <Stack direction={props.stackDirection ?? ['column', 'row']}>
              <Hide when={isEmpty(enabledRelativeDates)}>
                <Box padding={4}>
                  <DatePickerRelativeDates
                    relativeDates={enabledRelativeDates}
                    onChange={onChange}
                    value={props.value}
                    focusedIndex={focusedRelativeDayIndex}
                    onFocusChange={handleRelativeDayFocusChange}
                    onBlur={handleRelativeDayBlur}
                    onEscapeKey={handleRelativeDayEscape}
                    isFocused={relativeDaysFocused}
                    data-testid={testIdProps['data-testid']}
                    stackDirection={props.stackDirection}
                  />
                </Box>
                <Separator orientation={relativeDateSeparatorOrientation} spacing={0} />
              </Hide>
              <VStack grow={1} width={80}>
                <Box padding={4}>
                  <CalendarControl
                    activeMonth={activeMonth}
                    onChangeActiveMonth={setActiveMonth}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    currentFocusZone={focusTrap.currentFocusZone}
                    data-testid={testIdProps['data-testid']}
                  />
                </Box>
                <Separator spacing={0} />
                <Box padding={4}>
                  <CalendarView
                    activeMonth={activeMonth}
                    onChange={onChange}
                    value={props.value}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    disabledWeekdays={props.disabledWeekdays}
                    disabledDates={props.disabledDates}
                    focusedDate={calendarFocusedDate}
                    onFocusChange={setCalendarFocusedDate}
                    data-testid={testIdProps['data-testid']}
                  />
                </Box>
                <Separator spacing={0} />
                <Box padding={2}>
                  <HStack justify="flex-end">
                    <Button
                      variant="ghost"
                      size="small"
                      title={t('general.actions.close')}
                      onClick={() => closePopper()}
                      tabIndex={focusTrap.currentFocusZone === 'close-button' ? 0 : -1}
                      data-testid={suffixTestId('closeButton', testIdProps)}
                    />
                  </HStack>
                </Box>
              </VStack>
            </Stack>
          </Box>
        </div>
      </Popper>
    </div>
  );
}
